/**
 * UserMedicalCertificates.js
 * Component for managing medical certificates
 * - If userId is provided: shows certificates for specific user
 * - If userId is null/undefined: shows all certificates (admin view)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { 
  Card, 
  Table, 
  Button, 
  Badge, 
  Alert,
  Modal,
  Form,
  Row,
  Col,
  Spinner,
  Pagination
} from 'react-bootstrap';
import moment from 'moment';
import { medicalCertificateService } from '../../services/api';
import { uploadService } from '../../services/uploadService';
import {
  CERTIFICATE_TYPES,
  CERTIFICATE_TYPE_LABELS,
  CERTIFICATE_STATUS_LABELS,
  CERTIFICATE_STATUS_COLORS
} from '../../utils/constants';
import CertificateDataModal from '../Common/CertificateDataModal';
import FileViewer from '../Common/FileViewer';

const UserMedicalCertificates = ({ userId = null, status = 'all', onCertificateChange }) => {
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingCertificate, setEditingCertificate] = useState(null);
  const [statusFilter, setStatusFilter] = useState(status || 'all'); // 'all', 'valid', 'expiring', 'expired'
  const [archiveFilter, setArchiveFilter] = useState('active'); // 'active', 'all', 'archived'
  const [actionLoading, setActionLoading] = useState({});
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 5 // Default, will be adjusted based on context
  });

  const [formData, setFormData] = useState({
    certificateNumber: '',
    issueDate: '',
    expiryDate: '',
    certificateType: CERTIFICATE_TYPES.NON_COMPETITIVE,
    notes: ''
  });

  // Nuovi stati per il modal dati
  const [showDataModal, setShowDataModal] = useState(false);
  
  // Stato per il nuovo file da caricare nell'edit mode
  const [newFile, setNewFile] = useState(null);

  // Load certificates - either for specific user or all users
  const loadCertificates = useCallback(async (page = pagination.currentPage) => {
    try {
      setLoading(true);
      let response;
      const limit = userId ? 5 : 10; // Più elementi per pagina quando mostra tutti i certificati
      
      if (userId) {
        // Modalità utente specifico
        response = await medicalCertificateService.getUserCertificates(
          userId, 
          statusFilter, 
          page, 
          limit,
          false,
          archiveFilter
        );
      } else {
        // Modalità tutti gli utenti
        response = await medicalCertificateService.getAllCertificates(
          statusFilter,
          page, 
          limit,
          false,
          archiveFilter
        );
      }
      
      if (response.success) {
        setCertificates(response.data);
        setPagination(prev => ({
          ...prev,
          currentPage: response.pagination.currentPage,
          totalPages: response.pagination.totalPages,
          totalCount: response.pagination.totalCount,
          limit: limit
        }));
      } else {
        setError('Errore nel caricamento dei certificati');
      }
    } catch (err) {
      setError('Errore di connessione al server');
      console.error('Error loading certificates:', err);
    } finally {
      setLoading(false);
    }
  }, [userId, statusFilter, archiveFilter, pagination.currentPage]);

  useEffect(() => {
    loadCertificates(pagination.currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadCertificates]);

  // Handle page changes
  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, currentPage: newPage }));
  };

  const getCertificateStatus = (cert) => {
    if (!cert) return { label: 'Non presente', variant: 'danger' };
    const statusKey = cert.status;
    const label = CERTIFICATE_STATUS_LABELS[statusKey] || (statusKey === 'expired' ? 'Scaduto' : 'Valido');
    const variant = CERTIFICATE_STATUS_COLORS[statusKey] || (statusKey === 'expired' ? 'danger' : 'success');
    return { label, variant };
  };

  const getCertificateTypeLabel = (type) => {
    return CERTIFICATE_TYPE_LABELS[type] || type || '-';
  };

  const handleAddCertificate = () => {
    setEditingCertificate(null);
    setShowDataModal(true);
  };

  const handleDataSubmit = async (certificateData) => {
    try {
      setError('');
      setSuccess('');
      
      // Chiude il modal dei dati
      setShowDataModal(false);
      
      // Chiama direttamente il servizio di upload
      const response = await uploadService.uploadCertificate(certificateData);
      
      if (response.success) {
        setSuccess('Certificato medico creato con successo');
        loadCertificates(pagination.currentPage);
        onCertificateChange?.();
      } else {
        setError(response.message || 'Errore nella creazione del certificato');
      }
    } catch (error) {
      console.error('Error creating certificate:', error);
      setError('Errore di connessione al server');
    }
  };

  const handleEditCertificate = (cert) => {
    setEditingCertificate(cert);
    setFormData({
      certificateNumber: cert.certificateNumber,
      issueDate: cert.issueDate ? cert.issueDate.split('T')[0] : '',
      expiryDate: cert.expiryDate ? cert.expiryDate.split('T')[0] : '',
      certificateType: cert.certificateType,
      notes: cert.notes || ''
    });
    setNewFile(null); // Reset nuovo file
    setShowModal(true);
  };

  // Funzioni per gestire l'upload del nuovo file nell'edit mode
  const validateFile = (selectedFile) => {
    if (selectedFile.size > 10 * 1024 * 1024) {
      return 'Il file è troppo grande. Dimensione massima: 10MB';
    }

    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(selectedFile.type)) {
      return 'Tipo di file non supportato. Usa PDF, JPG o PNG.';
    }

    return null;
  };

  const handleNewFileChange = (e) => {
    const selectedFile = e.target.files[0];

    if (!selectedFile) {
      setNewFile(null);
      return;
    }

    const validationError = validateFile(selectedFile);
    if (validationError) {
      setError(validationError);
      setNewFile(null);
      return;
    }

    setNewFile(selectedFile);
    setError('');
  };

  const handleDeleteCertificate = async (certId, certNumber) => {
    if (window.confirm(`Sei sicuro di voler eliminare il certificato${certNumber ? ` ${certNumber}` : ''}?`)) {
      try {
        const response = await medicalCertificateService.deleteCertificate(certId);
        if (response.success) {
          setSuccess('Certificato eliminato con successo');
          loadCertificates(pagination.currentPage);
          onCertificateChange?.();
        } else {
          setError('Errore nell\'eliminazione del certificato');
        }
      } catch (err) {
        setError('Errore di connessione al server');
      }
    }
  };

  const handleArchiveCertificate = async (certId, certNumber, isArchived) => {
    const action = isArchived ? 'archiviare' : 'ripristinare';
    if (window.confirm(`Sei sicuro di voler ${action} il certificato${certNumber ? ` ${certNumber}` : ''}?`)) {
      const key = `archive-${certId}`;
      setActionLoading(prev => ({ ...prev, [key]: true }));
      try {
        const response = await medicalCertificateService.archiveCertificate(certId, isArchived);
        if (response.success) {
          setSuccess(response.message || (isArchived ? 'Certificato archiviato con successo' : 'Certificato ripristinato con successo'));
          loadCertificates(pagination.currentPage);
          onCertificateChange?.();
        } else {
          setError(response.message || `Errore durante l'operazione di ${action}`);
        }
      } catch (err) {
        setError('Errore di connessione al server');
      } finally {
        setActionLoading(prev => ({ ...prev, [key]: false }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setError('');
      setSuccess('');

      const submitData = {
        ...formData,
        user: userId
      };

      let response;
      if (editingCertificate) {
        // Modalità edit - usa la mutation di update unificata
        const uploadData = {
          ...submitData,
          userId: userId,
          certificateNumber: formData.certificateNumber,
          issueDate: formData.issueDate,
          expiryDate: formData.expiryDate,
          certificateType: formData.certificateType,
          notes: formData.notes,
          file: newFile // Può essere null se non c'è un nuovo file
        };
        
        response = await uploadService.updateCertificate(editingCertificate.id, uploadData);
      } else {
        // Modalità creazione
        response = await medicalCertificateService.createCertificate(submitData);
      }

      if (response.success) {
        setSuccess(
          editingCertificate 
            ? newFile 
              ? 'Certificato e file aggiornati con successo'
              : 'Certificato aggiornato con successo' 
            : 'Certificato creato con successo'
        );
        setShowModal(false);
        setNewFile(null);
        loadCertificates(pagination.currentPage);
        onCertificateChange?.();
      } else {
        setError(response.message || 'Errore nel salvataggio del certificato');
      }
    } catch (err) {
      setError('Errore di connessione al server');
      console.error('Error saving certificate:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Auto-generate expiry date (1 year from issue date)
  const handleIssueDateChange = (e) => {
    const issueDate = e.target.value;
    setFormData(prev => ({
      ...prev,
      issueDate
    }));
    
    if (issueDate && !formData.expiryDate) {
      const expiryDate = new Date(issueDate);
      expiryDate.setFullYear(expiryDate.getFullYear() + 1);
      setFormData(prev => ({
        ...prev,
        expiryDate: expiryDate.toISOString().split('T')[0]
      }));
    }
  };

  return (
    <div className="user-memberships">
      {/* Header dinamico */}
      {!userId && (
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2>
            <i className="fas fa-file-medical me-2"></i>
            Tutti i Certificati Medici
          </h2>
        </div>
      )}

      <Row className="mb-3 align-items-center">
        <Col md={userId ? 5 : 4} className="mb-2 mb-md-0">
          <Form.Group>
            <Form.Select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPagination(prev => ({ ...prev, currentPage: 1 }));
              }}
            >
              <option value="all">Tutti i Certificati</option>
              <option value="valid">Solo Validi</option>
              <option value="expiring">In Scadenza (30 giorni)</option>
              <option value="expired">Scaduti</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={userId ? 4 : 4} className="mb-2 mb-md-0">
          <div className="btn-group btn-group-sm w-100" role="group">
            <Button
              type="button"
              variant={archiveFilter === 'active' ? 'primary' : 'outline-secondary'}
              onClick={() => {
                setArchiveFilter('active');
                setPagination(prev => ({ ...prev, currentPage: 1 }));
              }}
            >
              Attivi
            </Button>
            <Button
              type="button"
              variant={archiveFilter === 'all' ? 'primary' : 'outline-secondary'}
              onClick={() => {
                setArchiveFilter('all');
                setPagination(prev => ({ ...prev, currentPage: 1 }));
              }}
            >
              Tutti
            </Button>
            <Button
              type="button"
              variant={archiveFilter === 'archived' ? 'warning' : 'outline-secondary'}
              onClick={() => {
                setArchiveFilter('archived');
                setPagination(prev => ({ ...prev, currentPage: 1 }));
              }}
            >
              <i className="fas fa-archive me-1"></i>
              Archiviati
            </Button>
          </div>
        </Col>
        <Col xs="auto" className="ms-auto">
          {userId && (
            <Button
              variant="primary"
              onClick={handleAddCertificate}
              disabled={loading}
            >
              <i className="fas fa-plus me-2"></i>
              Nuovo certificato
            </Button>
          )}
        </Col>
      </Row>
      
    <Card className="mt-4">
      <Card.Body>
        {error && (
          <Alert variant="danger" onClose={() => setError('')} dismissible>
            {error}
          </Alert>
        )}
        {success && (
          <Alert variant="success" onClose={() => setSuccess('')} dismissible>
            {success}
          </Alert>
        )}

        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Caricamento...</span>
            </Spinner>
          </div>
        ) : certificates.length > 0 ? (
          <>
            <Table responsive hover>
              <thead>
                <tr>
                  {!userId && <th>Utente</th>}
                  <th>Numero Certificato</th>
                  <th>Tipo</th>
                  <th>Data Rilascio</th>
                  <th>Scadenza</th>
                  <th>Status</th>
                  <th>Documento</th>
                  <th>Azioni</th>
                </tr>
              </thead>
              <tbody>
                {certificates.map((cert) => {
                  const status = getCertificateStatus(cert);
                  return (
                    <tr key={cert.id} className={cert.isArchived ? 'table-warning opacity-75' : ''}>
                      {!userId && (
                        <td>
                          <div>
                            <strong>
                              <a 
                                href={`/users/${cert.user.id}/edit`}
                                style={{ textDecoration: 'none' }}
                              >
                                {cert.user.firstName} {cert.user.lastName}
                              </a>
                            </strong>
                            <br />
                            <small className="text-muted">{cert.user.fiscalCode}</small>
                          </div>
                        </td>
                      )}
                      <td>
                        <strong>{cert.certificateNumber || '-'}</strong>
                        {cert.isArchived && (
                          <Badge bg="warning" text="dark" className="ms-2">
                            <i className="fas fa-archive me-1"></i>Archiviato
                          </Badge>
                        )}
                      </td>
                      <td>
                        <Badge bg="info" className="badge-role">
                          {getCertificateTypeLabel(cert.certificateType)}
                        </Badge>
                      </td>
                      <td>
                        {moment(cert.issueDate).format('DD/MM/YYYY')}
                      </td>
                      <td>
                        {moment(cert.expiryDate).format('DD/MM/YYYY')}
                      </td>
                      <td>
                        <Badge bg={status.variant} className="badge-status">
                          {status.label}
                        </Badge>
                      </td>
                      <td>
                        <FileViewer 
                          certificate={cert} 
                          type="certificate"
                          onDeleteFile={() => {
                            loadCertificates();
                            onCertificateChange?.();
                          }} 
                        />
                      </td>
                      <td>
                        <div className="d-flex align-items-center">
                          {userId && (
                            <Button
                              variant="outline-primary"
                              size="sm"
                              className="btn-action me-1"
                              onClick={() => handleEditCertificate(cert)}
                              title="Modifica"
                            >
                              <i className="fas fa-edit"></i>
                            </Button>
                          )}
                          <Button
                            variant={cert.isArchived ? 'outline-success' : 'outline-warning'}
                            size="sm"
                            className="btn-action me-1"
                            onClick={() => handleArchiveCertificate(cert.id, cert.certificateNumber, !cert.isArchived)}
                            disabled={actionLoading[`archive-${cert.id}`]}
                            title={cert.isArchived ? 'Ripristina' : 'Archivia'}
                          >
                            {actionLoading[`archive-${cert.id}`] ? (
                              <span className="spinner-border spinner-border-sm" role="status"></span>
                            ) : (
                              <i className={`fas fa-${cert.isArchived ? 'undo' : 'archive'}`}></i>
                            )}
                          </Button>
                          {userId && (
                            <Button
                              variant="outline-danger"
                              size="sm"
                              className="btn-action"
                              onClick={() => handleDeleteCertificate(cert.id, cert.certificateNumber)}
                              title="Elimina"
                            >
                              <i className="fas fa-trash"></i>
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
            
            {/* Paginazione */}
            {pagination.totalPages > 1 && (
              <div className="d-flex justify-content-between align-items-center mt-3">
                <div className="text-muted small">
                  Pagina {pagination.currentPage} di {pagination.totalPages} 
                  ({pagination.totalCount} certificati totali)
                </div>
                <Pagination size="sm">
                  <Pagination.First 
                    onClick={() => handlePageChange(1)}
                    disabled={pagination.currentPage === 1}
                  />
                  <Pagination.Prev 
                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                    disabled={pagination.currentPage === 1}
                  />
                  
                  {/* Page numbers */}
                  {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
                    .filter(page => 
                      page === 1 || 
                      page === pagination.totalPages || 
                      Math.abs(page - pagination.currentPage) <= 1
                    )
                    .map((page, index, array) => (
                      <React.Fragment key={page}>
                        {index > 0 && array[index - 1] < page - 1 && (
                          <Pagination.Ellipsis disabled />
                        )}
                        <Pagination.Item
                          active={page === pagination.currentPage}
                          onClick={() => handlePageChange(page)}
                        >
                          {page}
                        </Pagination.Item>
                      </React.Fragment>
                    ))
                  }
                  
                  <Pagination.Next 
                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                    disabled={pagination.currentPage === pagination.totalPages}
                  />
                  <Pagination.Last 
                    onClick={() => handlePageChange(pagination.totalPages)}
                    disabled={pagination.currentPage === pagination.totalPages}
                  />
                </Pagination>
              </div>
            )}
          </>
        ) : (statusFilter !== 'all' || archiveFilter !== 'active') ? (
          <div className="text-center py-4 text-muted">
            <i className="fas fa-filter fa-3x mb-3"></i>
            <p>Nessun certificato corrisponde ai filtri selezionati.</p>
            <Button 
              variant="outline-secondary" 
              onClick={() => {
                setStatusFilter('all');
                setArchiveFilter('active');
              }}
            >
              <i className="fas fa-times me-1"></i>
              Rimuovi Filtri
            </Button>
          </div>
        ) : (
          <div className="text-center py-4 text-muted">
            <i className="fas fa-file-medical fa-3x mb-3"></i>
            <p>Nessun certificato medico presente.</p>
          </div>
        )}
      </Card.Body>

      {/* Modal per aggiungere/modificare certificato */}
      <Modal show={showModal} onHide={() => { setShowModal(false); setNewFile(null); }} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingCertificate ? 'Modifica Certificato' : 'Nuovo Certificato'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Numero Certificato</Form.Label>
                  <Form.Control
                    type="text"
                    name="certificateNumber"
                    value={formData.certificateNumber}
                    onChange={handleInputChange}
                    placeholder="CERT-2024-001"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Tipo Certificato</Form.Label>
                  <Form.Select
                    name="certificateType"
                    value={formData.certificateType}
                    onChange={handleInputChange}
                  >
                    {Object.entries(CERTIFICATE_TYPE_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Data Rilascio *</Form.Label>
                  <Form.Control
                    type="date"
                    name="issueDate"
                    value={formData.issueDate}
                    onChange={handleIssueDateChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Data Scadenza *</Form.Label>
                  <Form.Control
                    type="date"
                    name="expiryDate"
                    value={formData.expiryDate}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Note Aggiuntive</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                placeholder="Note aggiuntive sul certificato..."
              />
            </Form.Group>

            {editingCertificate && (
              <Form.Group className="mb-3">
                <Form.Label>Documento Allegato</Form.Label>
                <div className="mb-3">
                  <FileViewer 
                    certificate={editingCertificate} 
                    type="certificate"
                    onDeleteFile={() => {
                      loadCertificates();
                      onCertificateChange?.();
                      // Aggiorna anche il certificato in editing per rimuovere i riferimenti al file
                      setEditingCertificate(prev => ({
                        ...prev,
                        fileName: null,
                        filePath: null,
                        fileSize: null
                      }));
                    }} 
                  />
                </div>
                
                {/* Sezione per caricare un nuovo file */}
                <hr className="my-3" />
                <h6 className="mb-3">
                  <i className="fas fa-upload me-2"></i>
                  Sostituisci con nuovo file
                </h6>
                
                <Form.Group className="mb-3">
                  <Form.Label>Carica nuovo documento (PDF, JPG, PNG)</Form.Label>
                  <Form.Control
                    type="file"
                    onChange={handleNewFileChange}
                    accept=".pdf,.jpg,.jpeg,.png"
                  />
                  <Form.Text className="text-muted">
                    Seleziona un nuovo file per sostituire quello esistente. Dimensione massima: 10MB.
                  </Form.Text>
                </Form.Group>

                {newFile && (
                  <div className="mb-3">
                    <small className="text-success">
                      <i className="fas fa-check-circle me-1"></i>
                      Nuovo file selezionato: {newFile.name} ({(newFile.size / 1024 / 1024).toFixed(2)} MB)
                    </small>
                  </div>
                )}
              </Form.Group>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => { setShowModal(false); setNewFile(null); }}>
              Annulla
            </Button>
            <Button type="submit" variant="primary">
              <i className={`fas ${editingCertificate ? 'fa-save' : 'fa-plus'} me-1`}></i>
              {editingCertificate ? 'Aggiorna' : 'Crea'} Certificato
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modal per raccogliere dati certificato */}
      <CertificateDataModal
        show={showDataModal}
        onHide={() => setShowDataModal(false)}
        onSubmit={handleDataSubmit}
        userId={userId} // Passa userId, può essere null per la modalità "tutti gli utenti"
      />
    </Card>
    </div>
  );
};

export default UserMedicalCertificates;
